import cn from 'classnames';
import React, {
  FC,
  ReactNode,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import debounce from 'lodash/debounce';

import Typography from '@mui/material/Typography';
import {
  ColumnDef,
  Row,
  SortingState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';

import sortIcon from '@assets/icons/sort.svg?url';
import sortAscIcon from '@assets/icons/sortAsc.svg?url';
import sortDescIcon from '@assets/icons/sortDesc.svg?url';

import Loader from '@components/common/Loader';

import { SortDestination } from '@constants/common/common.constants';
import { TableVariant } from '@constants/common/ui.constants';

import useStyles from './styles.module';
import {
  BOTTOM_BUFFER_HEIGHT,
  DEFAULT_TABLE_HEIGHT,
  HEADER_BUFFER_LOADING,
  OVERSCAN,
  ROW_HEIGHT,
} from './table.constants';
import { getCommonPinningStyles } from './table.helpers';

interface Props {
  columns: ColumnDef<any>[];
  data: any[];
  fetchNextPage?: () => Promise<void>;
  isFetching?: boolean;
  isLoading: boolean;
  rowHeight?: number;
  handleClickRow?: (row: Row<any>) => void;
  totalRowCount: number;
  tableHeight?: number;
  sorting?: SortingState;
  pinnedRightColumn?: string[];
  enableSorting?: boolean;
  orderBy?: string | null;
  sortDirection?: SortDestination;
  onSort?: (value: string) => void;
  variant?: TableVariant;
  noDataText?: ReactNode | null;
  className?: string;
}

const Table: FC<Props> = ({
  columns,
  data,
  className = '',
  fetchNextPage = undefined,
  isFetching = false,
  isLoading,
  rowHeight = undefined,
  handleClickRow = undefined,
  totalRowCount,
  tableHeight = DEFAULT_TABLE_HEIGHT,
  sorting = undefined,
  onSort = undefined,
  pinnedRightColumn = undefined,
  enableSorting = false,
  orderBy = '',
  noDataText = null,
  variant = TableVariant.Contained,
  sortDirection = SortDestination.Desc,
}) => {
  const { classes } = useStyles({ variant });

  const tableContainerRef = useRef<HTMLDivElement>(null);

  const totalFetched = useMemo(() => data.length, [data]);

  const fetchMoreOnBottomReached = useCallback(
    debounce(async (containerRefElement?: HTMLDivElement | null) => {
      if (!containerRefElement || isFetching || totalFetched >= totalRowCount) {
        return;
      }
      const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
      if (scrollHeight - scrollTop - clientHeight < BOTTOM_BUFFER_HEIGHT) {
        if (fetchNextPage) {
          await fetchNextPage();
        }
      }
    }, 300),
    [fetchNextPage, isFetching, totalFetched, totalRowCount],
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: {
      sorting,
      columnPinning: {
        right: pinnedRightColumn,
      },
    },
    manualSorting: true,
    getSortedRowModel: getSortedRowModel(),
    enableSorting,
  });

  const { rows } = table.getRowModel();

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: () => rowHeight || ROW_HEIGHT,
    getScrollElement: () => tableContainerRef.current,
    measureElement:
      typeof window !== 'undefined' &&
      navigator.userAgent.indexOf('Firefox') === -1
        ? (element: Element) => element?.getBoundingClientRect().height
        : undefined,
    overscan: OVERSCAN,
  });

  table.setOptions((prev) => ({
    ...prev,
  }));

  useEffect(() => {
    (async () => {
      await fetchMoreOnBottomReached(tableContainerRef.current);
    })();
  }, [fetchMoreOnBottomReached]);

  return (
    <div className={cn(classes.tableWrapper, className)}>
      <div
        className={classes.scrollableTableContainer}
        onScroll={(e) => fetchMoreOnBottomReached(e.target as HTMLDivElement)}
        ref={tableContainerRef}
        style={{ height: tableHeight, maxHeight: tableHeight }}
      >
        <table className={classes.table}>
          <thead className={classes.header}>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const { column } = header;

                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      className={classes.headerTh}
                      style={{
                        minWidth: column.getSize(),
                        ...getCommonPinningStyles(column),
                        background: 'transparent',
                        ...(column.columnDef.meta as any)?.style,
                      }}
                    >
                      <div
                        role="presentation"
                        onClick={() =>
                          onSort &&
                          header.column.getCanSort() &&
                          onSort(column.id)
                        }
                      >
                        <Typography
                          variant={
                            TableVariant.Secondary ? 'body2' : 'subtitle1'
                          }
                          color={(theme) => theme.secondary.s3}
                          className={cn({
                            [classes.headerCellSorted]:
                              header.column.getCanSort(),
                          })}
                        >
                          {header.column.getCanSort() &&
                            (orderBy === column.id ? (
                              sortDirection === SortDestination.Asc ? (
                                <img src={sortAscIcon} alt="" />
                              ) : (
                                <img src={sortDescIcon} alt="" />
                              )
                            ) : (
                              <img src={sortIcon} alt="" />
                            ))}
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                        </Typography>
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody
            style={{
              height: rowVirtualizer.getTotalSize()
                ? `${rowVirtualizer.getTotalSize()}px`
                : tableHeight && tableHeight - HEADER_BUFFER_LOADING,
            }}
          >
            {isLoading ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className={classes.loaderCell}
                  style={{ maxHeight: tableHeight }}
                >
                  <Loader size={36} />
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className={classes.noDataCell}>
                  {noDataText || 'No data'}
                </td>
              </tr>
            ) : (
              rowVirtualizer.getVirtualItems().map((virtualRow) => {
                const row = rows[virtualRow.index];

                return (
                  <tr
                    data-index={virtualRow.index}
                    ref={(node) => rowVirtualizer.measureElement(node)}
                    key={row.id}
                    className={classes.tableRow}
                    style={{
                      height: rowHeight || ROW_HEIGHT,
                      transform: `translateY(${virtualRow.start}px)`,
                    }}
                    onClick={() => handleClickRow && handleClickRow(row)}
                  >
                    {row.getVisibleCells().map((cell) => {
                      const { column } = cell;

                      return (
                        <td
                          key={cell.id}
                          style={{
                            minWidth: cell.column.getSize(),
                            ...getCommonPinningStyles(column),
                          }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default memo(Table);
